<template>
  <div ref="root" class="ss" :class="{ 'ss--open': open, 'ss--disabled': disabled }">
    <label v-if="label" class="ss__label">{{ label }}</label>
    <button ref="control" type="button" class="ss__control" :disabled="disabled" @click="toggle" @keydown.down.prevent="openList" @keydown.esc="close">
      <span v-if="selected" class="ss__value">
        <span class="ss__title">{{ selected.label }}</span>
        <span v-if="selected.sub" class="ss__sub">{{ selected.sub }}</span>
      </span>
      <span v-else class="ss__placeholder">{{ placeholder }}</span>
      <i class="fas fa-chevron-down ss__chevron"></i>
    </button>

    <!-- Список открывается поверх окна (fixed), поэтому не обрезается прокруткой формы -->
    <div v-if="open" ref="panel" class="ss__panel" :style="panelStyle" @keydown.esc.stop="close">
      <div class="ss__searchwrap">
        <i class="fas fa-search"></i>
        <input
          ref="search"
          v-model="query"
          class="ss__search"
          type="text"
          :placeholder="searchPlaceholder"
          autocomplete="off"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="chooseActive"
        />
      </div>
      <div ref="list" class="ss__list">
        <button v-if="clearLabel" type="button" class="ss__item ss__item--clear" :class="{ 'is-selected': !selected }" @click="choose(null)">
          <span class="ss__title">{{ clearLabel }}</span>
        </button>
        <button
          v-for="(opt, i) in filtered"
          :key="opt.value"
          type="button"
          class="ss__item"
          :class="{ 'is-selected': selected && selected.value === opt.value, 'is-active': i === active, 'is-disabled': opt.disabled }"
          :disabled="opt.disabled"
          @mouseenter="active = i"
          @click="choose(opt)"
        >
          <span class="ss__text">
            <span class="ss__title">{{ opt.label }}</span>
            <span v-if="opt.sub" class="ss__sub">{{ opt.sub }}</span>
          </span>
          <span v-if="opt.badge" class="ss__badge">{{ opt.badge }}</span>
        </button>
        <div v-if="!filtered.length" class="ss__empty">Ничего не найдено</div>
      </div>
    </div>
  </div>
</template>

<script>
// Выпадающий список с поиском: у вариантов есть ФИО (label), строка под ним (sub — телефон и т. п.), метка справа (badge)
// и поле search, по которому ищется. options: [{ value, label, sub?, badge?, search?, disabled? }]; value — значение выбранного.
export default {
  name: 'SearchSelect',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    label: { type: String, default: '' },
    placeholder: { type: String, default: 'Выберите…' },
    searchPlaceholder: { type: String, default: 'Поиск по ФИО или телефону…' },
    // Если задан — первым в списке стоит пункт «очистить выбор» с этим текстом (значение становится пустым).
    clearLabel: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return { open: false, query: '', active: 0, panelStyle: {} }
  },
  computed: {
    selected() {
      const v = this.value === null || this.value === undefined ? '' : String(this.value)
      if (v === '') return null
      return this.options.find((o) => String(o.value) === v) || null
    },
    filtered() {
      const words = this.query.trim().toLowerCase().split(/\s+/).filter(Boolean)
      if (!words.length) return this.options
      return this.options.filter((o) => {
        const text = (o.search || [o.label, o.sub].filter(Boolean).join(' ')).toLowerCase()
        return words.every((w) => text.indexOf(w) !== -1)
      })
    }
  },
  watch: {
    query() {
      this.active = 0
    }
  },
  beforeDestroy() {
    this.unbind()
  },
  methods: {
    toggle() {
      if (this.open) this.close()
      else this.openList()
    },
    openList() {
      if (this.disabled || this.open) return
      this.query = ''
      this.active = 0
      this.place()
      this.open = true
      this.bind()
      this.$nextTick(() => {
        if (this.$refs.search) this.$refs.search.focus()
      })
    },
    close() {
      if (!this.open) return
      this.open = false
      this.unbind()
      if (this.$refs.control) this.$refs.control.focus()
    },
    // Панель под полем, а если места снизу мало — над ним.
    place() {
      const rect = this.$refs.control.getBoundingClientRect()
      const below = window.innerHeight - rect.bottom
      const height = 340
      const top = below >= height || below >= rect.top
      this.panelStyle = {
        left: rect.left + 'px',
        width: rect.width + 'px',
        maxHeight: Math.max(200, Math.min(height, (top ? below : rect.top) - 12)) + 'px',
        ...(top ? { top: rect.bottom + 4 + 'px' } : { bottom: window.innerHeight - rect.top + 4 + 'px' })
      }
    },
    bind() {
      document.addEventListener('mousedown', this.onOutside, true)
      window.addEventListener('resize', this.close)
      window.addEventListener('scroll', this.onScroll, true)
    },
    unbind() {
      document.removeEventListener('mousedown', this.onOutside, true)
      window.removeEventListener('resize', this.close)
      window.removeEventListener('scroll', this.onScroll, true)
    },
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) this.close()
    },
    // Прокрутка страницы или окна закрывает список (он привязан к экрану); прокрутка самого списка — нет.
    onScroll(e) {
      if (this.$refs.panel && this.$refs.panel.contains(e.target)) return
      this.close()
    },
    move(step) {
      const max = this.filtered.length - 1
      if (max < 0) return
      let next = this.active
      for (let i = 0; i <= max; i++) {
        next = Math.min(max, Math.max(0, next + step))
        if (!this.filtered[next].disabled) break
      }
      this.active = next
      this.$nextTick(() => {
        const el = this.$refs.list && this.$refs.list.querySelectorAll('.ss__item:not(.ss__item--clear)')[next]
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    chooseActive() {
      const opt = this.filtered[this.active]
      if (opt && !opt.disabled) this.choose(opt)
    },
    choose(opt) {
      this.$emit('input', opt ? opt.value : '')
      this.close()
    }
  }
}
</script>

<style scoped>
.ss { position: relative; display: flex; flex-direction: column; gap: var(--space-2); }
.ss__label {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
}
.ss__control {
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: var(--space-6);
  box-sizing: border-box;
  padding: var(--space-3) var(--space-8);
  font-family: inherit;
  font-size: var(--text-base);
  color: var(--text-primary);
  text-align: left;
  background: var(--bg-input);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  border-radius: var(--radius-md);
  cursor: pointer;
}
.ss__control:hover:not(:disabled), .ss--open .ss__control { border-color: var(--border-focus); }
.ss__control:disabled { opacity: 0.6; cursor: not-allowed; }
.ss__value { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ss__placeholder { flex: 1; color: var(--text-muted); }
.ss__chevron { margin-left: auto; color: var(--text-tertiary); font-size: var(--text-xs); transition: transform var(--duration-fast) var(--ease-base); }
.ss--open .ss__chevron { transform: rotate(180deg); }

.ss__panel {
  position: fixed;
  z-index: calc(var(--z-modal) + 20);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--bg-surface);
  border: var(--border-width-default) solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
}
.ss__searchwrap {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  padding: var(--space-6) var(--space-8);
  border-bottom: var(--border-width-default) solid var(--border-default);
  color: var(--text-tertiary);
}
.ss__search {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  font-family: inherit;
  font-size: var(--text-base);
  color: var(--text-primary);
  background: transparent;
}
.ss__list { overflow-y: auto; padding: var(--space-2); }
.ss__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: var(--space-5) var(--space-8);
  font-family: inherit;
  text-align: left;
  color: var(--text-primary);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
}
.ss__item.is-active, .ss__item:hover:not(.is-disabled) { background: var(--bg-subtle); }
.ss__item.is-selected { background: var(--bg-selected); }
.ss__item.is-disabled { opacity: 0.55; cursor: not-allowed; }
.ss__item--clear { color: var(--text-secondary); font-style: italic; }
.ss__text { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ss__title { font-size: var(--text-base); font-weight: var(--font-weight-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ss__sub { font-size: var(--text-xs); color: var(--text-tertiary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ss__badge {
  flex-shrink: 0;
  padding: 1px var(--space-5);
  font-size: var(--text-2xs);
  color: var(--color-brand-800);
  background: var(--primary-soft-2);
  border-radius: var(--radius-pill);
}
.ss__empty { padding: var(--space-8); text-align: center; color: var(--text-tertiary); font-size: var(--text-sm); }
</style>
